import type { ColdLibraryStatus, ColdSweepResult } from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber } from '../components/data/data-text';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';

// Admin → Search index (task 16, PLAN §4 "Search"): how much of the Library and the names is indexed for text
// and for meaning, "Index now" (one sweep, the same work the half-hourly cron does) and "Rebuild" (the re-index job).

export const Route = createFileRoute('/admin/search')({ component: SearchIndexPage });

interface Counts {
  total: number;
  /** In the word index (passages; names always are). */
  indexed?: number;
  embedded: number;
}
interface Status {
  passages: Counts;
  names: Counts;
  pendingDeletions: number;
  vectorsAvailable: boolean;
}
interface RunResult {
  run: { deleted: number; indexed: number; passages: number; names: number; remaining: number };
  status: Status;
}

const TOUR = {
  id: 'admin-search',
  steps: [
    {
      target: '[data-tour="search-coverage"]',
      title: 'What is searchable',
      body: 'Every passage and name is searchable by its words as soon as it is added. The bars show how many are also searchable by meaning.',
      learnMore: 'search-index',
    },
    {
      target: '[data-tour="search-actions"]',
      title: 'Index now or rebuild',
      body: 'Index now does the next batch right away instead of waiting for the half-hourly run. Rebuild starts over, for example after the embedding model changes.',
    },
  ],
};

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

function SearchIndexPage() {
  useScreenTour(TOUR);
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const q = useQuery({ queryKey: ['admin', 'search-index'], queryFn: () => api<Status>('/admin/search-index') });
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };
  const done = (status: Status, text: string) => {
    qc.setQueryData(['admin', 'search-index'], status);
    setMessage({ kind: 'ok', text });
  };

  const run = useMutation({
    mutationFn: () => withReauth(() => api<RunResult>('/admin/search-index/run', { body: {} })),
    onSuccess: r => {
      const parts = [
        r.run.indexed && `${plural(r.run.indexed, 'passage')} added to the word index`,
        r.run.passages && `${plural(r.run.passages, 'passage')} embedded`,
        r.run.names && `${plural(r.run.names, 'name')} embedded`,
        r.run.deleted && `${plural(r.run.deleted, 'removed vector')} cleared`,
      ].filter(Boolean);
      const what = parts.length ? `Done: ${parts.join(', ')}.` : 'Nothing new to index.';
      const left = r.run.remaining ? ` ${plural(r.run.remaining, 'item')} still to go.` : '';
      done(r.status, `${what}${left}`);
    },
    onError,
  });
  const rebuild = useMutation({
    mutationFn: () =>
      withReauth(() => api<{ rebuilt: { passages: number; names: number }; status: Status }>('/admin/search-index/rebuild', { body: {} })),
    onSuccess: r =>
      done(
        r.status,
        `Rebuilt the word index: ${plural(r.rebuilt.passages, 'passage')} and ${plural(r.rebuilt.names, 'name')}. Meaning search catches up over the next runs.`,
      ),
    onError,
  });
  const askRebuild = async () => {
    setMessage(null);
    const ok = confirm
      ? await confirm({
          title: 'Rebuild the search index?',
          body: 'The word index is rebuilt right away. Every passage and name is then embedded again, a batch at a time, so meaning search is incomplete until that finishes. Nothing is deleted from the Library.',
          confirmLabel: 'Rebuild',
          cancelLabel: 'Cancel',
        })
      : true;
    if (ok) rebuild.mutate();
  };

  if (!q.data) return <PageHeader title="Search index" description="Loading…" />;
  const s = q.data;
  const busy = run.isPending || rebuild.isPending;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Search index"
        description="How the Library and the names of actors are indexed for search and chat: by their words, and by meaning."
      />
      <p className="text-sm text-muted-foreground" role="status">
        {s.vectorsAvailable
          ? 'Meaning search is on: new passages and names are embedded as they arrive, and every half hour for anything left over.'
          : 'Meaning search is not available in this environment (local development): search uses words only.'}
      </p>
      <Tip id="search-index-confidential" learnMore="search-index">
        Embeddings always use the same Cloudflare model, for standard and confidential content alike: nothing leaves Cloudflare to be
        indexed.
      </Tip>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      <section aria-labelledby="coverage-title" className="space-y-4" data-tour="search-coverage">
        <h2 id="coverage-title" className="font-semibold">
          Coverage
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Coverage label="Library passages" counts={s.passages} vectors={s.vectorsAvailable} />
          <Coverage label="Names of actors" counts={s.names} vectors={s.vectorsAvailable} />
        </div>
        {s.pendingDeletions > 0 && (
          <p className="text-sm text-muted-foreground">
            <DataNumber>{s.pendingDeletions.toLocaleString()}</DataNumber> vectors of removed passages or names wait to be cleared. They
            never show in results meanwhile.
          </p>
        )}
      </section>

      <section aria-labelledby="actions-title" className="space-y-3" data-tour="search-actions">
        <h2 id="actions-title" className="font-semibold">
          Maintenance
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              setMessage(null);
              run.mutate();
            }}
            disabled={busy}
          >
            {run.isPending ? 'Indexing…' : 'Index now'}
          </Button>
          <Button variant="outline" onClick={askRebuild} disabled={busy}>
            {rebuild.isPending ? 'Rebuilding…' : 'Rebuild index'}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Rebuild after the embedding model changes, or if search misses text you know is in the Library.
        </p>
        <LearnMore slug="search-index" />
      </section>

      <Retention />
    </div>
  );
}

/**
 * Library retention (scanning phase 1): after N months an automatically collected item that nothing cites leaves
 * the search index (its passages wait in storage), keeping its name, text and meaning. Off until switched on.
 */
function Retention() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const monthsId = useId();
  const q = useQuery({ queryKey: ['admin', 'retention'], queryFn: () => api<ColdLibraryStatus>('/admin/search-index/retention') });
  const [months, setMonths] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };
  const save = useMutation({
    mutationFn: (b: { enabled: boolean; months: number }) =>
      withReauth(() => api<ColdLibraryStatus>('/admin/search-index/retention', { method: 'PUT', body: b })),
    onSuccess: r => {
      qc.setQueryData(['admin', 'retention'], r);
      setMonths(null);
      setMessage({
        kind: 'ok',
        text: r.settings.enabled
          ? `Library retention is on: items older than ${plural(r.settings.months, 'month')} are moved to storage every night.`
          : 'Library retention is off: nothing is moved to storage.',
      });
    },
    onError,
  });
  const run = useMutation({
    mutationFn: () =>
      withReauth(() => api<{ run: ColdSweepResult; status: ColdLibraryStatus }>('/admin/search-index/retention/run', { body: {} })),
    onSuccess: r => {
      qc.setQueryData(['admin', 'retention'], r.status);
      setMessage({
        kind: 'ok',
        text: r.run.documents
          ? `Moved ${plural(r.run.documents, 'item')} to storage (${plural(r.run.passages, 'passage')}).${r.run.more ? ' More are due: the next run continues.' : ''}`
          : 'Nothing was due.',
      });
    },
    onError,
  });
  if (!q.data) return null;
  const s = q.data;
  const shown = months ?? String(s.settings.months);
  const n = Number(shown);
  const valid = Number.isInteger(n) && n >= s.limits.min && n <= s.limits.max;
  const toggle = async () => {
    setMessage(null);
    if (!s.settings.enabled && confirm) {
      const ok = await confirm({
        title: 'Switch on Library retention?',
        body: `Every night, items that came in automatically more than ${plural(s.settings.months, 'month')} ago and that nothing cites leave the search index: their passages wait in storage until someone brings them back. They are still found by name and by meaning. Uploaded files and evidence never move.`,
        confirmLabel: 'Switch on',
        cancelLabel: 'Cancel',
      });
      if (!ok) return;
    }
    save.mutate({ enabled: !s.settings.enabled, months: s.settings.months });
  };
  return (
    <section aria-labelledby="retention-title" className="space-y-3" data-tour="search-retention">
      <h2 id="retention-title" className="font-semibold">
        Library retention
      </h2>
      <p className="text-sm text-muted-foreground">
        Items collected automatically (website monitors, feeds, the ingest API) that no claim cites, in no Case, with no mention or link,
        leave the search index after a while. Their name, text and meaning stay; their words are found again once they are brought back.
        Uploaded files are never moved.
      </p>
      <p className="text-sm" role="status">
        {s.settings.enabled ? 'On' : 'Off'} · <DataNumber>{s.cold.toLocaleString()}</DataNumber> in storage ·{' '}
        <DataNumber>{s.due.toLocaleString()}</DataNumber>
        {s.capped ? '+' : ''} due now
      </p>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={e => {
          e.preventDefault();
          setMessage(null);
          if (valid) save.mutate({ enabled: s.settings.enabled, months: n });
        }}
      >
        <div className="space-y-1">
          <label htmlFor={monthsId} className="text-xs font-medium">
            Months before an item moves to storage
          </label>
          <Input
            id={monthsId}
            inputMode="numeric"
            className="w-24"
            value={shown}
            onChange={e => setMonths(e.target.value)}
            aria-invalid={!valid}
            aria-describedby={`${monthsId}-help`}
          />
          <p id={`${monthsId}-help`} className="text-xs text-muted-foreground">
            From {s.limits.min} to {s.limits.max}; {s.limits.defaultMonths} by default.
          </p>
        </div>
        <Button type="submit" variant="outline" disabled={!valid || save.isPending || n === s.settings.months}>
          Save months
        </Button>
        <Button type="button" onClick={() => void toggle()} disabled={save.isPending}>
          {s.settings.enabled ? 'Switch off' : 'Switch on'}
        </Button>
        {s.settings.enabled && (
          <Button type="button" variant="outline" onClick={() => run.mutate()} disabled={run.isPending}>
            {run.isPending ? 'Moving…' : 'Run now'}
          </Button>
        )}
      </form>
      <LearnMore slug="library-retention" />
    </section>
  );
}

function Coverage({ label, counts, vectors }: { label: string; counts: Counts; vectors: boolean }) {
  const pct = counts.total ? Math.round((counts.embedded / counts.total) * 100) : 100;
  return (
    <div className="space-y-2 rounded-lg border bg-surface p-4 text-sm">
      <p className="font-medium">{label}</p>
      <p>
        By words: <DataNumber>{(counts.indexed ?? counts.total).toLocaleString()}</DataNumber> of{' '}
        <DataNumber>{counts.total.toLocaleString()}</DataNumber>
      </p>
      <p>
        By meaning: <DataNumber>{counts.embedded.toLocaleString()}</DataNumber> of <DataNumber>{counts.total.toLocaleString()}</DataNumber>
        {!vectors && <span className="text-muted-foreground"> (not available here)</span>}
      </p>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label={`${label}: searchable by meaning`}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
